(() => {
  // Hidden slides ship without src so they are never fetched eagerly; swap in the
  // real URL only for the slide that is visible plus the one queued behind it.
  const guard = (img) => {
    if (img.dataset.appImgGuard) return;
    img.dataset.appImgGuard = '1';
    img.addEventListener('error', () => {
      if (!img.getAttribute('srcset')) return;
      const full = img.dataset.src || img.getAttribute('src');
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      if (full) img.src = full;
    }, { once: true });
  };
  const hydrate = (slide) => {
    if (!slide) return;
    const img = slide.querySelector('img[data-src], img[data-srcset]');
    if (!img) return;
    guard(img);
    const full = img.dataset.src;
    const srcset = img.dataset.srcset;
    img.loading = 'eager';
    if (srcset && 'srcset' in img) {
      img.srcset = srcset;
      if (img.dataset.sizes) img.sizes = img.dataset.sizes;
    }
    if (full) img.src = full;
    delete img.dataset.src;
    delete img.dataset.srcset;
    delete img.dataset.sizes;
  };
  // Slides carry aria-hidden, but a browser without `inert` would still let the
  // hidden slide's link take keyboard focus. Mirror the inert state in tabindex.
  const setFocusable = (slide, active) => {
    const nodes = [];
    if (slide.hasAttribute('href')) nodes.push(slide);
    slide.querySelectorAll('a[href], button').forEach((node) => nodes.push(node));
    nodes.forEach((node) => {
      if (active) node.removeAttribute('tabindex');
      else node.setAttribute('tabindex', '-1');
    });
  };
  // Swipe left/right over the slides. A gesture that reads as vertical is handed
  // straight back to the browser, so page scrolling is never hijacked.
  const SWIPE_START = 10;
  const SWIPE_COMMIT = 48;
  const attachSwipe = (viewport, onChange) => {
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let axis = null;
    const owns = (event) => pointerId !== null && event.pointerId === pointerId;
    const reset = () => {
      pointerId = null;
      axis = null;
      viewport.classList.remove('is-swiping');
    };
    viewport.addEventListener('pointerdown', (event) => {
      if (pointerId !== null) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerId = event.pointerId;
      axis = null;
      startX = event.clientX;
      startY = event.clientY;
    }, { passive: true });
    window.addEventListener('pointermove', (event) => {
      if (!owns(event)) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (axis !== null) return;
      if (Math.abs(dx) < SWIPE_START && Math.abs(dy) < SWIPE_START) return;
      if (Math.abs(dx) > Math.abs(dy)) {
        axis = 'x';
        viewport.classList.add('is-swiping');
      } else {
        reset();
      }
    }, { passive: true });
    window.addEventListener('pointerup', (event) => {
      if (!owns(event)) return;
      const dx = event.clientX - startX;
      const horizontal = axis === 'x';
      reset();
      if (horizontal && Math.abs(dx) >= SWIPE_COMMIT) onChange(dx < 0 ? 1 : -1);
    });
    window.addEventListener('pointercancel', (event) => {
      if (owns(event)) reset();
    });
  };
  function init(root) {
    if (root.dataset.appHeroReady) return;
    root.dataset.appHeroReady = '1';
    const slides = [...root.querySelectorAll('[data-app-slide]')];
    const dots = [...root.querySelectorAll('[data-app-dot]')];
    slides.forEach((slide) => {
      const img = slide.querySelector('.app-hero__image');
      if (img) guard(img);
    });
    if (slides.length < 2) return;
    let current = 0, timer, suppressClick = false;
    const show = (i) => {
      current = (i + slides.length) % slides.length;
      slides.forEach((slide, n) => {
        const active = n === current;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if ('inert' in slide) slide.inert = !active;
        setFocusable(slide, active);
      });
      dots.forEach((dot, n) => dot.setAttribute('aria-current', String(n === current)));
      hydrate(slides[current]);
      hydrate(slides[(current + 1) % slides.length]);
    };
    const pause = () => clearInterval(timer);
    const play = () => {
      pause();
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches) timer = setInterval(() => show(current + 1), 6000);
    };
    const goTo = (i) => { show(i); play(); };
    const step = (dir, fromSwipe) => {
      if (fromSwipe) {
        suppressClick = true;
        setTimeout(() => { suppressClick = false; }, 400);
      }
      goTo(current + dir);
    };
    root.querySelector('[data-app-prev]')?.addEventListener('click', () => step(-1));
    root.querySelector('[data-app-next]')?.addEventListener('click', () => step(1));
    dots.forEach((dot, n) => dot.addEventListener('click', () => goTo(n)));
    // A swipe finishes on top of the slide link, so swallow the click it releases.
    root.addEventListener('click', (event) => {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);
    root.addEventListener('mouseenter', pause);
    root.addEventListener('mouseleave', play);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', (event) => {
      if (event.relatedTarget && root.contains(event.relatedTarget)) return;
      play();
    });
    const viewport = root.querySelector('.app-hero__viewport');
    if (viewport) attachSwipe(viewport, (dir) => step(dir, true));
    document.addEventListener('visibilitychange', () => document.hidden ? pause() : play());
    show(0);
    play();
  }
  document.querySelectorAll('[data-app-hero]').forEach(init);
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-app-hero]').forEach(init));
})();
